// src/pages/User/components/PreferencesModals.jsx
import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { updateUserByAdminThunk } from "../../../store/thunks/userThunks";
import Modal from "../../../components/common/Modal";
import Button from "../../../components/common/Button";
import Checkbox from "../../../components/common/Checkbox";
import {
  CalendarIcon,
  ClockIcon,
  BellIcon,
  MailIcon,
  PhoneIcon,
  ChatAlt2Icon,
} from "@heroicons/react/outline";
import { notifySuccess, notifyError } from "../../../utils/notification";

// ============================================
// Appointment Preferences Modal
// ============================================
export const AppointmentPreferencesModal = ({
  isOpen,
  onClose,
  userId,
  user,
}) => {
  const dispatch = useDispatch();

  // Default state with all required nested objects
  const defaultPreferences = {
    preferredDays: [],
    preferredTimeSlot: "ANY",
    preferredTime: "",
    reminderSettings: {
      enabled: true,
      methods: ["SMS"],
      advanceTime: 60,
      sendMultipleReminders: false,
    },
    preferredConsultationType: "IN_PERSON",
    specialRequirements: {
      requiresPrivateRoom: false,
      requiresWheelchairAccess: false,
      prefersFemaleStaff: false,
      languagePreference: "",
      other: "",
    },
  };

  const [preferences, setPreferences] = useState(defaultPreferences);

  useEffect(() => {
    if (user?.appointmentPreferences) {
      // Merge user preferences with defaults to ensure all properties exist
      setPreferences({
        ...defaultPreferences,
        ...user.appointmentPreferences,
        reminderSettings: {
          ...defaultPreferences.reminderSettings,
          ...(user.appointmentPreferences.reminderSettings || {}),
        },
        specialRequirements: {
          ...defaultPreferences.specialRequirements,
          ...(user.appointmentPreferences.specialRequirements || {}),
        },
      });
    }
  }, [user]);

  const handleSave = async () => {
    try {
      await dispatch(
        updateUserByAdminThunk({
          userId,
          userData: { appointmentPreferences: preferences },
        })
      );
      notifySuccess("Appointment preferences updated");
      onClose();
    } catch (error) {
      notifyError("Failed to update preferences");
    }
  };

  const daysOfWeek = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  const timeSlots = ["MORNING", "AFTERNOON", "EVENING", "ANY"];
  const consultationTypes = ["IN_PERSON", "VIDEO_CALL", "PHONE_CALL", "ANY"];
  const reminderMethods = ["SMS", "EMAIL", "WHATSAPP", "CALL"];

  const toggleDay = (day) => {
    setPreferences({
      ...preferences,
      preferredDays: preferences.preferredDays.includes(day)
        ? preferences.preferredDays.filter((d) => d !== day)
        : [...preferences.preferredDays, day],
    });
  };

  const toggleReminderMethod = (method) => {
    setPreferences({
      ...preferences,
      reminderSettings: {
        ...preferences.reminderSettings,
        methods: preferences.reminderSettings.methods.includes(method)
          ? preferences.reminderSettings.methods.filter((m) => m !== method)
          : [...preferences.reminderSettings.methods, method],
      },
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      closeModal={onClose}
      title={
        <div className="flex items-center gap-2">
          <CalendarIcon className="w-6 h-6 text-primary" />
          Appointment Preferences
        </div>
      }
      isFullScreen={false}
    >
      <div className="space-y-6 max-h-[70vh] overflow-y-auto">
        {/* Preferred Days */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Preferred Days
          </label>
          <div className="flex flex-wrap gap-2">
            {daysOfWeek.map((day) => (
              <button
                key={day}
                onClick={() => toggleDay(day)}
                className={`px-4 py-2 rounded-lg text-sm font-medium capitalize ${
                  preferences.preferredDays.includes(day)
                    ? "bg-primary text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {day.slice(0, 3)}
              </button>
            ))}
          </div>
        </div>

        {/* Preferred Time Slot */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Preferred Time Slot
          </label>
          <div className="grid grid-cols-2 gap-2">
            {timeSlots.map((slot) => (
              <button
                key={slot}
                onClick={() =>
                  setPreferences({ ...preferences, preferredTimeSlot: slot })
                }
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  preferences.preferredTimeSlot === slot
                    ? "bg-primary text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {slot.replace("_", " ")}
              </button>
            ))}
          </div>
        </div>

        {/* Consultation Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Preferred Consultation Type
          </label>
          <div className="grid grid-cols-2 gap-2">
            {consultationTypes.map((type) => (
              <button
                key={type}
                onClick={() =>
                  setPreferences({
                    ...preferences,
                    preferredConsultationType: type,
                  })
                }
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  preferences.preferredConsultationType === type
                    ? "bg-primary text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {type.replace("_", " ")}
              </button>
            ))}
          </div>
        </div>

        {/* Reminder Settings */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <BellIcon className="w-5 h-5 text-blue-600" />
            Reminder Settings
          </h4>

          <div className="space-y-3">
            <Checkbox
              label="Enable Reminders"
              checked={preferences.reminderSettings.enabled}
              onChange={(e) =>
                setPreferences({
                  ...preferences,
                  reminderSettings: {
                    ...preferences.reminderSettings,
                    enabled: e.target.checked,
                  },
                })
              }
            />

            {preferences.reminderSettings.enabled && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reminder Methods
                  </label>
                  <div className="space-y-2">
                    {reminderMethods.map((method) => (
                      <Checkbox
                        key={method}
                        label={method}
                        checked={preferences.reminderSettings.methods.includes(
                          method
                        )}
                        onChange={() => toggleReminderMethod(method)}
                      />
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Remind (minutes before)
                  </label>
                  <select
                    value={preferences.reminderSettings.advanceTime}
                    onChange={(e) =>
                      setPreferences({
                        ...preferences,
                        reminderSettings: {
                          ...preferences.reminderSettings,
                          advanceTime: Number(e.target.value),
                        },
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value={30}>30 minutes</option>
                    <option value={60}>1 hour</option>
                    <option value={120}>2 hours</option>
                    <option value={1440}>1 day</option>
                  </select>
                </div>

                <Checkbox
                  label="Send Multiple Reminders"
                  checked={preferences.reminderSettings.sendMultipleReminders}
                  onChange={(e) =>
                    setPreferences({
                      ...preferences,
                      reminderSettings: {
                        ...preferences.reminderSettings,
                        sendMultipleReminders: e.target.checked,
                      },
                    })
                  }
                />
              </>
            )}
          </div>
        </div>

        {/* Special Requirements */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Special Requirements
          </label>
          <div className="space-y-2">
            <Checkbox
              label="Requires Private Room"
              checked={preferences.specialRequirements.requiresPrivateRoom}
              onChange={(e) =>
                setPreferences({
                  ...preferences,
                  specialRequirements: {
                    ...preferences.specialRequirements,
                    requiresPrivateRoom: e.target.checked,
                  },
                })
              }
            />
            <Checkbox
              label="Requires Wheelchair Access"
              checked={preferences.specialRequirements.requiresWheelchairAccess}
              onChange={(e) =>
                setPreferences({
                  ...preferences,
                  specialRequirements: {
                    ...preferences.specialRequirements,
                    requiresWheelchairAccess: e.target.checked,
                  },
                })
              }
            />
            <Checkbox
              label="Prefers Female Staff"
              checked={preferences.specialRequirements.prefersFemaleStaff}
              onChange={(e) =>
                setPreferences({
                  ...preferences,
                  specialRequirements: {
                    ...preferences.specialRequirements,
                    prefersFemaleStaff: e.target.checked,
                  },
                })
              }
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Save Preferences
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// ============================================
// Communication Preferences Modal
// ============================================
export const CommunicationPreferencesModal = ({
  isOpen,
  onClose,
  userId,
  user,
}) => {
  const dispatch = useDispatch();

  const defaultPreferences = {
    smsNotifications: true,
    emailNotifications: true,
    whatsappNotifications: false,
    pushNotifications: true,
    notificationTypes: {
      appointments: true,
      offers: true,
      newArrivals: false,
      priceDrops: false,
      orderUpdates: true,
      abandonedCart: true,
    },
    preferredContactTime: "ANY",
  };

  const [preferences, setPreferences] = useState(defaultPreferences);

  useEffect(() => {
    if (user?.communicationPreferences) {
      setPreferences({
        ...defaultPreferences,
        ...user.communicationPreferences,
        notificationTypes: {
          ...defaultPreferences.notificationTypes,
          ...(user.communicationPreferences.notificationTypes || {}),
        },
      });
    }
  }, [user]);

  const handleSave = async () => {
    try {
      await dispatch(
        updateUserByAdminThunk({
          userId,
          userData: { communicationPreferences: preferences },
        })
      );
      notifySuccess("Communication preferences updated");
      onClose();
    } catch (error) {
      notifyError("Failed to update preferences");
    }
  };

  const contactTimes = ["MORNING", "AFTERNOON", "EVENING", "ANY"];

  return (
    <Modal
      isOpen={isOpen}
      closeModal={onClose}
      title={
        <div className="flex items-center gap-2">
          <ChatAlt2Icon className="w-6 h-6 text-primary" />
          Communication Preferences
        </div>
      }
      isFullScreen={false}
    >
      <div className="space-y-6 max-h-[70vh] overflow-y-auto">
        {/* Communication Channels */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-gray-800 mb-3">
            Notification Channels
          </h4>
          <div className="space-y-3">
            <Checkbox
              label={
                <div className="flex items-center gap-2">
                  <PhoneIcon className="w-4 h-4" />
                  <span>SMS Notifications</span>
                </div>
              }
              checked={preferences.smsNotifications}
              onChange={(e) =>
                setPreferences({
                  ...preferences,
                  smsNotifications: e.target.checked,
                })
              }
            />
            <Checkbox
              label={
                <div className="flex items-center gap-2">
                  <MailIcon className="w-4 h-4" />
                  <span>Email Notifications</span>
                </div>
              }
              checked={preferences.emailNotifications}
              onChange={(e) =>
                setPreferences({
                  ...preferences,
                  emailNotifications: e.target.checked,
                })
              }
            />
            <Checkbox
              label={
                <div className="flex items-center gap-2">
                  <ChatAlt2Icon className="w-4 h-4" />
                  <span>WhatsApp Notifications</span>
                </div>
              }
              checked={preferences.whatsappNotifications}
              onChange={(e) =>
                setPreferences({
                  ...preferences,
                  whatsappNotifications: e.target.checked,
                })
              }
            />
            <Checkbox
              label={
                <div className="flex items-center gap-2">
                  <BellIcon className="w-4 h-4" />
                  <span>Push Notifications</span>
                </div>
              }
              checked={preferences.pushNotifications}
              onChange={(e) =>
                setPreferences({
                  ...preferences,
                  pushNotifications: e.target.checked,
                })
              }
            />
          </div>
        </div>

        {/* Notification Types */}
        <div>
          <h4 className="font-semibold text-gray-800 mb-3">
            Notification Types
          </h4>
          <div className="space-y-2">
            {Object.entries(preferences.notificationTypes).map(
              ([key, value]) => (
                <Checkbox
                  key={key}
                  label={key
                    .replace(/([A-Z])/g, " $1")
                    .replace(/^./, (str) => str.toUpperCase())}
                  checked={value}
                  onChange={(e) =>
                    setPreferences({
                      ...preferences,
                      notificationTypes: {
                        ...preferences.notificationTypes,
                        [key]: e.target.checked,
                      },
                    })
                  }
                />
              )
            )}
          </div>
        </div>

        {/* Preferred Contact Time */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
            <ClockIcon className="w-4 h-4" />
            Preferred Contact Time
          </label>
          <div className="grid grid-cols-2 gap-2">
            {contactTimes.map((time) => (
              <button
                key={time}
                onClick={() =>
                  setPreferences({ ...preferences, preferredContactTime: time })
                }
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  preferences.preferredContactTime === time
                    ? "bg-primary text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {time}
              </button>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Save Preferences
          </Button>
        </div>
      </div>
    </Modal>
  );
};
